import React, { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button, Spinner, Card, Alert } from "reactstrap";
import Head from "../../layout/head/Head";
import Content from "../../layout/content/Content";
import {
  Block,
  BlockHead,
  BlockHeadContent,
  BlockTitle,
  BlockDes,
  PreviewCard,
  Icon,
} from "../../components/Component";
import { message, Spin } from "antd";
import { useForm } from "react-hook-form";
import quotationApis from "../../api/master/quotations";
import authService from "../../api/auth/authService";
import Swal from "sweetalert2";
import PublicQuotationForm from "./PublicQuotationForm";
import SupplierRegistration from "./SupplierRegistration";
import OtpInputScreen from "./OtpInputScreen";
import Cookies from "js-cookie";
import LogoDark2x from "../../images/logo-dark2x.png";

interface QuotationData {
  materials: any;
  quotationHeader: any;
}

const PublicQuotation: React.FC = () => {
  const { linkId } = useParams<{ linkId: string }>();
  const navigate = useNavigate();
  const {
    register,
    handleSubmit: handleFormSubmit,
    formState: { errors },
    watch,
  } = useForm();
  const [loading, setLoading] = useState(false);
  const [quotationData, setQuotationData] = useState<QuotationData | null>(
    null
  );
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [showQuotationForm, setShowQuotationForm] = useState(false);
  const [showRegistrationForm, setShowRegistrationForm] = useState(false);
  const [showOtpScreen, setShowOtpScreen] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [pendingEmailOrPhone, setPendingEmailOrPhone] = useState<string>("");
  const [pendingName, setPendingName] = useState<string>("");
  const [pendingEmail, setPendingEmail] = useState<string>("");
  const [pendingPhone, setPendingPhone] = useState<string>("");
  const [otpSent, setOtpSent] = useState(false);
  const [tokenExpires, setTokenExpires] = useState<string | null>(null);
  const [buyerOrgId, setBuyerOrgId] = useState<number | null>(null);

  // Check for existing public-token on page load/refresh
  useEffect(() => {
    const checkExistingToken = () => {
      const publicToken = Cookies.get("public-token");
      if (publicToken) {
        try {
          const tokenData = JSON.parse(publicToken);
          // Check if token has expiration info
          if (tokenData.expires) {
            const expiresDate = new Date(tokenData.expires);
            const now = new Date();
            if (now >= expiresDate) {
              // Token expired, clear it
              Cookies.remove("public-token");
              Cookies.remove("public-refresh-token");
              return;
            }
            setTokenExpires(tokenData.expires);
          }

          // Restore user info from sessionStorage if available
          const savedUserInfo = sessionStorage.getItem(
            "public-quotation-userInfo"
          );
          const savedPendingEmail = sessionStorage.getItem(
            "public-quotation-pendingEmail"
          );
          const savedPendingPhone = sessionStorage.getItem(
            "public-quotation-pendingPhone"
          );

          if (savedUserInfo) {
            setUserInfo(JSON.parse(savedUserInfo));
          }
          if (savedPendingEmail) {
            setPendingEmail(savedPendingEmail);
          }
          if (savedPendingPhone) {
            setPendingPhone(savedPendingPhone);
          }

          // If token exists and is valid, user is authenticated
          setIsAuthenticated(true);
          setShowQuotationForm(true);
        } catch (error) {
          console.error("Error parsing public token:", error);
          // If token is invalid, clear it
          Cookies.remove("public-token");
          Cookies.remove("public-refresh-token");
        }
      }
    };

    checkExistingToken();
  }, []);

  useEffect(() => {
    if (linkId) {
      fetchQuotationData();
    }
  }, [linkId]);

  // Listen for session expired event from API calls
  useEffect(() => {
    const handleSessionExpiredEvent = () => {
      handleSessionExpired();
    };

    window.addEventListener(
      "public-session-expired",
      handleSessionExpiredEvent
    );
    return () => {
      window.removeEventListener(
        "public-session-expired",
        handleSessionExpiredEvent
      );
    };
  }, []);

  // Check token expiration
  const checkTokenExpiration = (): boolean => {
    if (!tokenExpires) return false;
    const expiresDate = new Date(tokenExpires);
    const now = new Date();
    if (now >= expiresDate) {
      // Token expired
      handleSessionExpired();
      return true;
    }
    return false;
  };

  // Handle session expired
  const handleSessionExpired = () => {
    message.warning("Session expired. Please verify again.");
    // Clear public token (not main app token)
    Cookies.remove("public-token");
    Cookies.remove("public-refresh-token");
    setTokenExpires(null);
    // Clear sessionStorage
    sessionStorage.removeItem("public-quotation-userInfo");
    sessionStorage.removeItem("public-quotation-pendingEmail");
    sessionStorage.removeItem("public-quotation-pendingPhone");
    // Reset to initial state - show email/phone login form
    setIsAuthenticated(false);
    setShowQuotationForm(false);
    setShowOtpScreen(false);
    setShowRegistrationForm(false);
    setUserInfo(null);
    setPendingEmailOrPhone("");
    setPendingEmail("");
    setPendingPhone("");
    setPendingName("");
    setOtpSent(false);
  };

  // Check token expiration periodically
  useEffect(() => {
    if (tokenExpires && isAuthenticated) {
      const interval = setInterval(() => {
        checkTokenExpiration();
      }, 60000); // Check every minute
      return () => clearInterval(interval);
    }
  }, [tokenExpires, isAuthenticated]);

  // Check token expiration before showing quotation form
  useEffect(() => {
    if (showQuotationForm && tokenExpires) {
      if (checkTokenExpiration()) {
        return;
      }
    }
  }, [showQuotationForm, tokenExpires]);

  // Cleanup: Clear public link token when component unmounts (user navigates away)
  useEffect(() => {
    return () => {
      // Only clear token if it's a public link token (check if we're on public link route)
      if (window.location.pathname.includes("/public-links/rfq/")) {
        // Don't clear on unmount if user is still on the page
        // Token will be cleared on session expiry or explicit logout
      }
    };
  }, []);

  const fetchQuotationData = async () => {
    try {
      setLoading(true);
      if (!linkId) {
        Swal.fire("Error", "Link ID is required", "error");
        setLoading(false);
        return;
      }
      const response = await quotationApis.publicGetByLinkId(linkId);

      // Handle response structure: { status, message, response: { quotationId, rfqId, lastSubmissionDate, orgId } }
      if (response?.status && response?.response) {
        const linkData = response.response;
        // Store link validation data - quotationId will be used later for fetching quotation data
        if (linkData.quotationId) {
          // Store buyerOrgId from response
          if (linkData.orgId) {
            setBuyerOrgId(linkData.orgId);
          }
          // Store quotationId for later use - quotation data will be fetched when needed
          setQuotationData({
            materials: [],
            quotationHeader: {
              quotationId: linkData.quotationId,
              rfqId: linkData.rfqId,
              lastSubmissionDate: linkData.lastSubmissionDate,
            },
          });
          setLoading(false);
        } else {
          console.error(
            "No quotationId in link validation response:",
            response
          );
          Swal.fire("Error", "Invalid link or quotation not found", "error");
          setLoading(false);
        }
      } else {
        Swal.fire("Error", "Quotation not found or invalid link", "error");
        setLoading(false);
      }
    } catch (error: any) {
      console.error("Error fetching quotation:", error);
      setLoading(false);
      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Failed to load quotation";
      Swal.fire("Error", errorMessage, "error");
    }
  };

  const createUserAccount = async (emailOrPhone: string, name: string) => {
    try {
      const isEmail = emailOrPhone.includes("@");
      const userData = {
        email: isEmail ? emailOrPhone : undefined,
        phone: !isEmail ? emailOrPhone.replace(/[^0-9]/g, "") : undefined,
        name: name,
        applicationType: "Seller",
        linkId: linkId, // Link to the quotation
      };

      const {
        response,
        status,
        message: apiMessage,
      } = await quotationApis.createSupplierAccount(userData);
      if (status) {
        return response?.data;
      } else {
        throw new Error(apiMessage || "Failed to create account");
      }
    } catch (error: any) {
      console.error("Error creating user:", error);
      throw error;
    }
  };

  const sendOtpToPhone = async (phoneNumber: string, email?: string) => {
    try {
      const { status, message: apiMessage } = await quotationApis.sendOtp(
        phoneNumber,
        email || pendingEmail || "",
        "Seller"
      );
      if (status) {
        message.success("OTP sent to your mobile number");
        setOtpSent(true);
        return true;
      } else {
        throw new Error(apiMessage || "Failed to send OTP");
      }
    } catch (error: any) {
      console.error("Error sending OTP:", error);
      Swal.fire("Error", error.message || "Failed to send OTP", "error");
      return false;
    }
  };

  const onSubmit = async (values: any) => {
    try {
      setLoading(true);
      const { email, phoneNumber } = values;

      if (!phoneNumber) {
        message.error("Phone number is required");
        setLoading(false);
        return;
      }

      // Store email and phone for later use
      setPendingEmail(email);
      setPendingPhone(phoneNumber);

      // Call sendOtp API directly - it will tell us if user exists or not
      try {
        const response = await quotationApis.sendOtp(phoneNumber, email || "", "Seller");

        // If response is successful with status: true and message: "success"
        if (response?.status === true && response?.message === "success") {
          // User exists - OTP sent, show OTP screen
          message.success("OTP sent to your mobile number");
          setOtpSent(true);
          setPendingEmailOrPhone(email || phoneNumber);
          setUserInfo({ email, phoneNumber });
          setShowOtpScreen(true);
        } else {
          // Check the error message from response
          const errorMessage =
            response?.message ||
            response?.response?.data?.message ||
            response?.response?.message ||
            "";

          // Only redirect to registration if error explicitly says "user not found"
          const isUserNotFound =
            errorMessage.toLowerCase().includes("user not found") ||
            errorMessage.toLowerCase().includes("usernot found") ||
            errorMessage.toLowerCase().includes("user does not exist");

          if (isUserNotFound) {
            // User doesn't exist - show SweetAlert first, then registration form
            setLoading(false);
            Swal.fire({
              icon: "info",
              title: "Registration Required",
              text: "User not found. Please register to continue.",
              confirmButtonText: "OK",
              confirmButtonColor: "#3085d6",
            }).then((result) => {
              if (result.isConfirmed) {
                setPendingEmailOrPhone(email || phoneNumber);
                setPendingName("");
                setShowRegistrationForm(true);
              }
            });
          } else {
            // Other error - show error message and don't redirect to registration
            setLoading(false);
            Swal.fire(
              "Error",
              errorMessage || "Failed to send OTP. Please try again.",
              "error"
            );
          }
        }
      } catch (error: any) {
        // Check if the error message explicitly says user not found
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          error?.response?.data?.error ||
          error?.response?.message ||
          "";

        // Only redirect to registration if error explicitly says "user not found"
        const isUserNotFound =
          errorMessage.toLowerCase().includes("user not found") ||
          errorMessage.toLowerCase().includes("usernot found") ||
          errorMessage.toLowerCase().includes("user does not exist");

        if (isUserNotFound) {
          // User doesn't exist - show SweetAlert first, then registration form
          setLoading(false);
          Swal.fire({
            icon: "info",
            title: "Registration Required",
            text: "User not found. Please register to continue.",
            confirmButtonText: "OK",
            confirmButtonColor: "#3085d6",
          }).then((result) => {
            if (result.isConfirmed) {
              setPendingEmailOrPhone(email || phoneNumber);
              setPendingName("");
              setShowRegistrationForm(true);
            }
          });
        } else {
          // Other error - show error message and don't redirect to registration
          setLoading(false);
          Swal.fire(
            "Error",
            errorMessage || "Failed to send OTP. Please try again.",
            "error"
          );
        }
      }
    } catch (error) {
      console.error("Error in handleSubmit:", error);
      Swal.fire("Error", "An error occurred. Please try again.", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleQuotationSubmit = (status: string) => {
    message.success("Quotation submitted successfully!");
    setShowQuotationForm(false);
    // Optionally redirect or show success message
  };

  const handleRegistrationComplete = async (registrationData: any) => {
    try {
      setLoading(true);
      // Extract email or phone from registration data
      const emailOrPhone =
        registrationData.email ||
        registrationData.phoneNumber ||
        pendingEmailOrPhone;
      const name = registrationData.contactPerson || pendingName;
      const phoneNumber = registrationData.phoneNumber;

      message.success("Registration completed successfully!");
      // Store only essential data for OTP flow
      setPendingEmail(registrationData.email);
      setPendingPhone(phoneNumber);
      setPendingEmailOrPhone(emailOrPhone);
      const newUserInfo = {
        email: registrationData.email,
        phoneNumber,
        name,
      };
      setUserInfo(newUserInfo);

      // Send OTP and show OTP screen
      if (phoneNumber) {
        const otpSent = await sendOtpToPhone(
          phoneNumber,
          registrationData.email
        );
        if (otpSent) {
          setShowRegistrationForm(false);
          setShowOtpScreen(true);
        }
      } else {
        // If no phone number, proceed directly (fallback)
        setIsAuthenticated(true);
        setShowRegistrationForm(false);
        setShowQuotationForm(true);
      }
    } catch (error: any) {
      console.error("Error completing registration:", error);
      Swal.fire(
        "Error",
        error.message || "Failed to complete registration. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleOtpVerification = async (otp: string) => {
    try {
      setLoading(true);
      const phoneNumber = pendingPhone || userInfo?.phoneNumber;

      if (!phoneNumber) {
        Swal.fire("Error", "Phone number not found", "error");
        return;
      }

      const response = await quotationApis.verifyOtp(phoneNumber, otp);

      if (response?.status && response?.data) {
        // Store the access token from OTP verification
        const tokenData = response.data.token;
        const expires = response.data.expires;
        if (tokenData?.access_token) {
          // Store token in public-token cookie (separate from main app token)
          const tokenToStore = {
            ...tokenData,
            expires: expires || tokenData.expires,
          };
          Cookies.set("public-token", JSON.stringify(tokenToStore), {
            expires: 7,
            secure: true,
            sameSite: "Strict",
          });
          if (tokenData.refresh_token) {
            Cookies.set("public-refresh-token", tokenData.refresh_token, {
              expires: 7,
              secure: true,
              sameSite: "Strict",
            });
          }
          // Store expiration date for checking
          if (expires) {
            setTokenExpires(expires);
          }
        }

        // Save user info to sessionStorage for page refresh
        if (userInfo) {
          sessionStorage.setItem(
            "public-quotation-userInfo",
            JSON.stringify(userInfo)
          );
        }
        if (pendingEmail) {
          sessionStorage.setItem("public-quotation-pendingEmail", pendingEmail);
        }
        if (pendingPhone) {
          sessionStorage.setItem("public-quotation-pendingPhone", pendingPhone);
        }

        message.success("OTP verified successfully!");
        setIsAuthenticated(true);
        setShowOtpScreen(false);
        setShowQuotationForm(true);
      } else {
        throw new Error(response?.message || "Invalid OTP");
      }
    } catch (error: any) {
      console.error("Error verifying OTP:", error);
      Swal.fire(
        "Error",
        error.message || "Invalid OTP. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleResendOtp = async () => {
    const phoneNumber = pendingPhone || userInfo?.phoneNumber;
    const email = pendingEmail || userInfo?.email;
    if (phoneNumber) {
      await sendOtpToPhone(phoneNumber, email);
    }
  };

  if (loading && !quotationData) {
    return (
      <>
        <Head title="Public Quotation" />
        <Content>
          <div
            className="d-flex justify-content-center align-items-center"
            style={{ minHeight: "60vh" }}
          >
            <Spin size="large" />
          </div>
        </Content>
      </>
    );
  }

  if (!quotationData) {
    return (
      <>
        <Head title="Public Quotation" />
        <Content>
          <Card className="card-bordered">
            <Alert color="danger" className="alert-icon">
              <Icon name="alert-circle" />
              <strong>Quotation Not Found</strong>
              <p className="mb-0">
                The quotation you are looking for does not exist or has been
                removed.
              </p>
            </Alert>
          </Card>
        </Content>
      </>
    );
  }

  if (showOtpScreen) {
    return (
      <>
        <Head title="OTP Verification" />
        <div className="login-container pt-2">
          <div
            style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}
          >
            <Block className="nk-block-middle nk-auth-body wide-xs">
              <div className="brand-logo pb-4 text-center">
                <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
                  <img className="" src={LogoDark2x} alt="logo" />
                </Link>
              </div>
              <PreviewCard
                className="card-bordered"
                bodyClass="card-inner-lg"
                style={{
                  position: "relative",
                  zIndex: 10,
                  pointerEvents: "auto",
                }}
              >
                <BlockHead>
                  <BlockHeadContent>
                    <BlockTitle tag="h4">OTP Verification</BlockTitle>
                    <BlockDes>
                      <p>
                        Enter the OTP sent to your mobile number{" "}
                        <strong>{pendingPhone || userInfo?.phoneNumber}</strong>
                      </p>
                    </BlockDes>
                  </BlockHeadContent>
                </BlockHead>
                <Block>
                  <div
                    className="alert alert-info alert-icon"
                    style={{ marginBottom: "24px" }}
                  >
                    <Icon name="info" />
                    <strong>OTP Sent</strong> Please check your mobile number
                    for the OTP.
                  </div>
                  <OtpInputScreen
                    onVerify={handleOtpVerification}
                    onResend={handleResendOtp}
                    onBack={() => {
                      setShowOtpScreen(false);
                      setOtpSent(false);
                      // Reset form values to allow user to enter different email/phone
                      setPendingEmail("");
                      setPendingPhone("");
                      setUserInfo(null);
                    }}
                    phoneNumber={pendingPhone || userInfo?.phoneNumber}
                    loading={loading}
                  />
                </Block>
              </PreviewCard>
            </Block>
          </div>
          <div className="login-bgi"></div>
        </div>
      </>
    );
  }

  if (showRegistrationForm) {
    return (
      <>
        <Head title="Supplier Registration" />
        <div className="login-container pt-2">
          <div className="d-flex justify-content-center">
            <div
              style={{
                width: "100%",
                maxWidth: "1200px",
                position: "relative",
                zIndex: 10,
                pointerEvents: "auto",
              }}
            >
              <SupplierRegistration
                onComplete={handleRegistrationComplete}
                onBack={() => {
                  setShowRegistrationForm(false);
                  setPendingEmailOrPhone("");
                  setPendingName("");
                }}
                initialEmailOrPhone={pendingEmailOrPhone}
                initialName={pendingName}
                buyerOrgId={buyerOrgId}
              />
            </div>
          </div>
          <div className="login-bgi"></div>
        </div>
      </>
    );
  }

  if (showQuotationForm && quotationData) {
    // Check token expiration before showing quotation form
    if (tokenExpires) {
      const expiresDate = new Date(tokenExpires);
      const now = new Date();
      if (now >= expiresDate) {
        handleSessionExpired();
        // Return null to prevent rendering until state updates
        return null;
      }
    }

    return (
      <PublicQuotationForm
        quotationId={
          quotationData?.quotationHeader?.quotationId || linkId || ""
        }
        rfqId={quotationData.quotationHeader.rfqId}
        userInfo={userInfo}
        quotationData={quotationData}
        onSuccess={() => {
          // Don't hide the form - let fetchOrderDetails() switch to view mode
          // The form will automatically show in view mode after fetchOrderDetails() is called
          // Success message is already shown in createInvoice via Swal.fire
        }}
        onBack={() => {
          setShowQuotationForm(false);
          setIsAuthenticated(false);
        }}
      />
    );
  }

  return (
    <>
      <Head title="Public Quotation" />
      <div className="login-container pt-2">
        <div
          style={{ position: "relative", zIndex: 10, pointerEvents: "auto" }}
        >
          <Block className="nk-block-middle nk-auth-body wide-xs">
            <div className="brand-logo pb-4 text-center">
              <Link to={process.env.PUBLIC_URL + "/"} className="logo-link">
                <img className="" src={LogoDark2x} alt="logo" />
              </Link>
            </div>
            <PreviewCard
              className="card-bordered"
              bodyClass="card-inner-lg"
              style={{
                position: "relative",
                zIndex: 10,
                pointerEvents: "auto",
              }}
            >
              <BlockHead>
                <BlockHeadContent>
                  <BlockTitle tag="h4">Quotation Access</BlockTitle>
                  <BlockDes>
                    <p>
                      Access Quotation -{" "}
                      <strong>{quotationData.quotationHeader.rfqId}</strong>
                    </p>
                  </BlockDes>
                </BlockHeadContent>
              </BlockHead>
              <Block>
                <div
                  className="alert alert-info alert-icon"
                  style={{ marginBottom: "24px" }}
                >
                  <Icon name="info" />
                  <strong>Enter your email and phone number.</strong> We'll
                  check if you have an account. If not, we'll create one for
                  you.
                </div>
                <form
                  className="is-alter"
                  onSubmit={handleFormSubmit(onSubmit)}
                  style={{
                    position: "relative",
                    zIndex: 10,
                    pointerEvents: "auto",
                  }}
                >
                  <div className="form-group">
                    <div className="form-label-group">
                      <label className="form-label" htmlFor="email">
                        Email
                      </label>
                    </div>
                    <div className="form-control-wrap">
                      <input
                        type="email"
                        id="email"
                        {...register("email", {
                          required: "Please enter your email",
                          pattern: {
                            value:
                              /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,24}$/,
                            message: "Please enter a valid email address",
                          },
                        })}
                        placeholder="Enter email address"
                        className="form-control-lg form-control"
                        autoComplete="off"
                        style={{ pointerEvents: "auto", height: "48px" }}
                      />
                      {errors.email && (
                        <span className="invalid">
                          {errors.email.message as string}
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="form-group">
                    <div className="form-label-group">
                      <label className="form-label" htmlFor="phoneNumber">
                        Phone Number
                      </label>
                    </div>
                    <div className="form-control-wrap">
                      <input
                        type="tel"
                        id="phoneNumber"
                        {...register("phoneNumber", {
                          required: "Please enter your phone number",
                          pattern: {
                            value: /^[6-9]\d{9}$/,
                            message:
                              "Please enter a valid 10-digit phone number",
                          },
                        })}
                        placeholder="Enter phone number"
                        className="form-control-lg form-control"
                        autoComplete="off"
                        style={{ pointerEvents: "auto", height: "48px" }}
                        maxLength={10}
                        onInput={(e: any) => {
                          e.target.value = e.target.value.replace(
                            /[^0-9]/g,
                            ""
                          );
                        }}
                      />
                      {errors.phoneNumber && (
                        <span className="invalid">
                          {errors.phoneNumber.message as string}
                        </span>
                      )}
                      <div className="form-note mt-2">
                        <Icon name="info" className="text-info" /> OTP will be
                        shared to the mobile number
                      </div>
                    </div>
                  </div>

                  <div className="form-group">
                    <Button
                      type="submit"
                      color="primary"
                      size="lg"
                      className="btn-block"
                      disabled={loading}
                      style={{
                        pointerEvents: "auto",
                        cursor: loading ? "not-allowed" : "pointer",
                      }}
                    >
                      {loading ? (
                        <>
                          <Spinner size="sm" color="light" className="me-2" />
                          Processing...
                        </>
                      ) : (
                        "Continue"
                      )}
                    </Button>
                  </div>
                  <div className="form-group text-center mt-3">
                    <div className="form-note">
                      Don't have an account?{" "}
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault();
                          const formValues = watch();
                          setPendingEmailOrPhone(
                            formValues.email || formValues.phoneNumber || ""
                          );
                          setPendingName("");
                          setShowRegistrationForm(true);
                        }}
                        style={{
                          color: "#5263FF",
                          textDecoration: "underline",
                          cursor: "pointer",
                        }}
                      >
                        Register here
                      </a>
                    </div>
                  </div>
                </form>
              </Block>
            </PreviewCard>
          </Block>
        </div>
        <div className="login-bgi"></div>
      </div>
    </>
  );
};

export default PublicQuotation;
